import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, useActiveShow, RideStatusBadge } from "common-fe";
import { isPlaceholderEntityName } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { classNumberLabel, sortByClassNumber } from "../util/classDisplay";
import useRepointRide from "../hooks/useRepointRide";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// #837 — the "Ride Editor" re-point tool. Named RidePointer here (not
// RideEditor) because scoreadmin-fe already has a page called RideEditor at
// /rides (card #508's per-ScoreSheet mark editor) — a different feature that
// this would otherwise clobber. This tool is the ONE operation that fixes
// every "scores are on the wrong ride" case: scribe scored the wrong class,
// an emergency manual ride, an orphaned sheet, or an FVD RO/ROO class-split
// ride. ScoreSheets are keyed by rideId (scoreSheetsByRide GSI), so the fix
// is always the same: re-point a ride's sheets A -> B, chosen by a human.
//
// Server-side authority: apps/score-api/src/handlers/ride.js `repointRide`
// (POST /ride/:rideId/repoint), which generalizes #388's resolveManualRide.
// This page enforces the same eligibility rules client-side (only an EMPTY,
// same-test ride is selectable as a target) so a mismatch never reaches the
// server, but the server 409s the same way regardless (race, or a bypassed
// client) — see useRepointRide.
//
// Raw classId editing is deliberately NOT offered anywhere in this tool.
// Relabeling a ride's classId in place needs no target and trivially passes
// a same-ride test check (the sheets never move), but would let a test-X
// ride's sheets sit under a test-Y class — class.testId is only a hint,
// ride->test is the authoritative edge (#435) — silently corrupting that
// class's placings with no way to detect it. Re-point-to-an-existing-real-
// FVD-ride is the ONLY supported move.
export default function RidePointer() {
  const { t } = useTranslation("scoreadmin");
  useDocumentTitle(t("title.ridePointer"));
  const scoreApi = useScoreApi();
  const { activeShowId, loading: loadingShow } = useActiveShow();
  const { repoint, repointing } = useRepointRide();

  const [classes, setClasses] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [testsById, setTestsById] = useState({});

  const [sourceClassId, setSourceClassId] = useState("");
  const [sourceRides, setSourceRides] = useState([]);
  const [loadingSourceRides, setLoadingSourceRides] = useState(false);
  const [sourceRideId, setSourceRideId] = useState("");

  const [targetClassId, setTargetClassId] = useState("");
  const [targetRides, setTargetRides] = useState([]);
  const [loadingTargetRides, setLoadingTargetRides] = useState(false);
  const [targetStatusById, setTargetStatusById] = useState({});
  const [targetRideId, setTargetRideId] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Classes + tests for the active show, shared by both the source and
  // target pickers (one show at a time — same convention as RideEditor's
  // RidePicker and AttachRideDialog).
  useEffect(() => {
    setClasses([]);
    setTestsById({});
    if (!activeShowId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingClasses(true);
        const [classList, tests] = await Promise.all([
          scoreApi.classes.listClasses(activeShowId).then(unwrap),
          scoreApi.tests.listTests(activeShowId).then(unwrap).catch(() => []),
        ]);
        if (cancelled) return;
        setClasses(sortByClassNumber(classList));
        setTestsById(Object.fromEntries((tests || []).map((tst) => [tst.id, tst])));
      } finally {
        if (!cancelled) setLoadingClasses(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeShowId, scoreApi]);

  // A real, non-placeholder, not-already-resolved-away ride — the only kind
  // either picker should ever offer. #585 drops FVD break/lunch placeholders;
  // resolvedIntoRideId (#388/#837) marks a ride whose sheets already moved
  // elsewhere, so it's audit-only from here on.
  const isSelectable = useCallback(
    (r) =>
      !isPlaceholderEntityName(r.horseName) &&
      !isPlaceholderEntityName(r.riderName) &&
      !r.resolvedIntoRideId,
    [],
  );

  useEffect(() => {
    setSourceRides([]);
    setSourceRideId("");
    if (!sourceClassId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingSourceRides(true);
        const list = unwrap(await scoreApi.rides.listRides(sourceClassId));
        if (!cancelled) setSourceRides((list || []).filter(isSelectable));
      } finally {
        if (!cancelled) setLoadingSourceRides(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceClassId, scoreApi, isSelectable]);

  useEffect(() => {
    setTargetRides([]);
    setTargetStatusById({});
    setTargetRideId("");
    if (!targetClassId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingTargetRides(true);
        const list = unwrap(await scoreApi.rides.listRides(targetClassId));
        const eligible = (list || []).filter(isSelectable);
        if (cancelled) return;
        setTargetRides(eligible);
        if (eligible.length) {
          const statuses = unwrap(
            await scoreApi.rides.batchRideStatus({ rideIds: eligible.map((r) => r.id) }),
          );
          if (!cancelled) {
            setTargetStatusById(Object.fromEntries(statuses.map((s) => [s.rideId, s])));
          }
        }
      } finally {
        if (!cancelled) setLoadingTargetRides(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetClassId, scoreApi, isSelectable]);

  const sourceRide = useMemo(
    () => sourceRides.find((r) => r.id === sourceRideId) || null,
    [sourceRides, sourceRideId],
  );

  // #837 AC7 — idnRideID is FVD's own ride id, but the cloud Ride entity
  // doesn't carry it today (and per docs/score/class-move-orphan-runbook.md,
  // FVD mints a NEW idnRideID per (CNO, class) anyway — it doesn't survive an
  // RO/ROO class split, which is exactly why the original #837 auto-detection
  // was dropped in favor of a human picking). The nearest available signal is
  // competitorNumber (same rider/horse pair carried across the move) — used
  // PURELY to pre-select a likely row as a starting point. It only ever
  // pre-fills targetRideId; the operator still has to review it and press
  // Confirm, exactly like AttachRideDialog's CNO-based "suggested" hint.
  const eligibility = useMemo(() => {
    const map = new Map();
    for (const r of targetRides) {
      if (r.id === sourceRideId) {
        map.set(r.id, { eligible: false, reason: "sameRide" });
        continue;
      }
      const status = targetStatusById[r.id];
      const hasScores = status?.hasScores ?? false;
      const sameTest = !!sourceRide && !!r.testId && r.testId === sourceRide.testId;
      if (!sourceRide) {
        map.set(r.id, { eligible: false, reason: "pickSourceFirst" });
      } else if (hasScores) {
        map.set(r.id, { eligible: false, reason: "alreadyScored" });
      } else if (!sameTest) {
        map.set(r.id, { eligible: false, reason: "differentTest" });
      } else {
        map.set(r.id, { eligible: true, reason: null });
      }
    }
    return map;
  }, [targetRides, targetStatusById, sourceRide, sourceRideId]);

  useEffect(() => {
    if (!sourceRide || targetRideId) return;
    const hint = targetRides.find(
      (r) =>
        eligibility.get(r.id)?.eligible &&
        sourceRide.competitorNumber != null &&
        String(r.competitorNumber) === String(sourceRide.competitorNumber),
    );
    if (hint) setTargetRideId(hint.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetRides, eligibility, sourceRide]);

  const targetRide = useMemo(
    () => targetRides.find((r) => r.id === targetRideId) || null,
    [targetRides, targetRideId],
  );
  const targetEligible = targetRideId ? !!eligibility.get(targetRideId)?.eligible : false;

  const rideLabel = (r) => {
    const cno = r.competitorNumber != null ? String(r.competitorNumber) : null;
    const who = [r.riderName, r.horseName].filter(Boolean).join(" / ") || null;
    return [cno ? `#${cno}` : null, who].filter(Boolean).join(" — ") || r.id;
  };

  // #837 — the RO/ROO scenario this tool exists for has the SAME rider/horse/
  // CNO on both sides (same competitor, moved to a different class by FVD),
  // so rideLabel() alone can't tell "From" and "To" apart in the confirm
  // dialog. Append the class so a look-alike pair is still distinguishable.
  const classLabelOf = (classId) => {
    const c = classes.find((cl) => cl.id === classId);
    return c ? classNumberLabel(c) : classId;
  };

  // #1878 — prefer the catalog's own clean name over FVD's raw testCode.
  const testLabel = (testId) =>
    testsById[testId]?.name || testsById[testId]?.testCode || testId || t("ridePointer.noTest");

  const reasonLabel = (reason) => {
    switch (reason) {
      case "alreadyScored":
        return t("ridePointer.reasonAlreadyScored");
      case "differentTest":
        return t("ridePointer.reasonDifferentTest");
      case "sameRide":
        return t("ridePointer.reasonSameRide");
      default:
        return t("ridePointer.reasonPickSourceFirst");
    }
  };

  const handleConfirm = async () => {
    if (!sourceRideId || !targetRideId || !targetEligible) return;
    setActionError(null);
    try {
      const result = await repoint(sourceRideId, targetRideId);
      setConfirmOpen(false);
      setSuccessMessage(
        t("ridePointer.success", { count: result.reassignedSheets ?? 0 }),
      );
      // Re-fetch the source class's rides so the now-resolved ride drops out
      // (resolvedIntoRideId is set server-side; isSelectable filters it here
      // too — the FE "hides" it exactly as the card requires).
      const list = unwrap(await scoreApi.rides.listRides(sourceClassId));
      setSourceRides((list || []).filter(isSelectable));
      setSourceRideId("");
      setTargetClassId("");
      setTargetRides([]);
      setTargetRideId("");
    } catch (err) {
      const code = err?.response?.data?.code;
      if (code === "target_already_scored") {
        setActionError({ message: t("ridePointer.serverAlreadyScored") });
      } else if (code === "test_mismatch") {
        setActionError({ message: t("ridePointer.serverMismatch") });
      } else {
        setActionError({ message: err?.serverErrorDetail || err?.message || t("ridePointer.serverError") });
      }
      setConfirmOpen(false);
    }
  };

  if (loadingShow) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom color="text.primary">
          {t("ridePointer.title")}
        </Typography>
        <TableSkeleton rows={3} columns={1} />
      </Box>
    );
  }

  return (
    <Box data-testid="ride-pointer-page" sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t("ridePointer.title")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        {t("ridePointer.subtitle")}
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        {t("ridePointer.classIdWarning")}
      </Alert>

      {!activeShowId ? (
        <Typography color="text.secondary">{t("ring.noShows")}</Typography>
      ) : (
        <Stack spacing={3}>
          {successMessage && (
            <Alert severity="success" onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)}>
              {actionError.message}
            </Alert>
          )}

          {/* Step 1 — source ride: the one whose scores are on the wrong ride. */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom color="text.primary">
              {t("ridePointer.sourceStepTitle")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ maxWidth: 720 }}>
              <FormControl size="small" fullWidth disabled={loadingClasses}>
                <InputLabel id="source-class-label">{t("ridePointer.sourceClassLabel")}</InputLabel>
                <Select
                  labelId="source-class-label"
                  label={t("ridePointer.sourceClassLabel")}
                  value={sourceClassId}
                  onChange={(e) => setSourceClassId(e.target.value)}
                >
                  {classes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {classNumberLabel(c)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth disabled={!sourceClassId || loadingSourceRides}>
                <InputLabel id="source-ride-label">{t("ridePointer.sourceRideLabel")}</InputLabel>
                <Select
                  labelId="source-ride-label"
                  label={t("ridePointer.sourceRideLabel")}
                  value={sourceRideId}
                  onChange={(e) => setSourceRideId(e.target.value)}
                >
                  {sourceRides.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {rideLabel(r)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {sourceClassId && !loadingSourceRides && sourceRides.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {t("ridePointer.noRides")}
              </Typography>
            )}
            {sourceRide && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("ridePointer.sourceTestLabel")}: {testLabel(sourceRide.testId)}
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Step 2 — target ride: an EXISTING, real FVD ride in the class the
              scores should have landed in. Never fabricated/relabeled — this
              tool only re-points onto a ride reconcile already ingested. */}
          <Paper
            data-testid="ride-pointer-target-panel"
            variant="outlined"
            sx={{ p: 2, opacity: sourceRide ? 1 : 0.5 }}
          >
            <Typography variant="h6" gutterBottom color="text.primary">
              {t("ridePointer.targetStepTitle")}
            </Typography>
            <FormControl
              size="small"
              sx={{ maxWidth: 360, mb: 2 }}
              fullWidth
              disabled={!sourceRide || loadingClasses}
            >
              <InputLabel id="target-class-label">{t("ridePointer.targetClassLabel")}</InputLabel>
              <Select
                labelId="target-class-label"
                label={t("ridePointer.targetClassLabel")}
                value={targetClassId}
                onChange={(e) => setTargetClassId(e.target.value)}
              >
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {classNumberLabel(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {loadingTargetRides ? (
              <TableSkeleton rows={3} columns={5} />
            ) : targetClassId && targetRides.length === 0 ? (
              <Typography color="text.secondary">{t("ridePointer.noRides")}</Typography>
            ) : targetRides.length > 0 ? (
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>{t("ridePointer.columns.cno")}</TableCell>
                      <TableCell>{t("ridePointer.columns.horse")}</TableCell>
                      <TableCell>{t("ridePointer.columns.rider")}</TableCell>
                      <TableCell>{t("ridePointer.columns.status")}</TableCell>
                      <TableCell>{t("ridePointer.columns.test")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {targetRides.map((r) => {
                      const info = eligibility.get(r.id) || { eligible: false, reason: null };
                      const status = targetStatusById[r.id]?.status ?? null;
                      // Ineligible rows stay real <TableRow> siblings (never
                      // wrapped in a Tooltip's <span>) — a <tr> nested inside
                      // a <span> is invalid HTML, and browsers drop it out of
                      // the table's shared column-width computation, which
                      // visibly breaks column alignment for every OTHER row.
                      // The always-visible reason Chip below already covers
                      // the AC ("shown but disabled, with a reason"), so a
                      // hover tooltip isn't needed.
                      return (
                        <TableRow
                          key={r.id}
                          hover={info.eligible}
                          selected={r.id === targetRideId}
                          onClick={() => info.eligible && setTargetRideId(r.id)}
                          sx={{
                            cursor: info.eligible ? "pointer" : "not-allowed",
                            opacity: info.eligible ? 1 : 0.55,
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Radio
                              checked={r.id === targetRideId}
                              disabled={!info.eligible}
                              onChange={() => info.eligible && setTargetRideId(r.id)}
                            />
                          </TableCell>
                          <TableCell>{r.competitorNumber ?? "-"}</TableCell>
                          <TableCell>{r.horseName || "-"}</TableCell>
                          <TableCell>{r.riderName || "-"}</TableCell>
                          <TableCell>
                            <RideStatusBadge status={status} />
                          </TableCell>
                          <TableCell>
                            {testLabel(r.testId)}
                            {!info.eligible && info.reason && (
                              <Chip
                                size="small"
                                color="default"
                                label={reasonLabel(info.reason)}
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : null}
          </Paper>

          <Box>
            <Button
              variant="contained"
              disabled={!sourceRideId || !targetRideId || !targetEligible || repointing}
              onClick={() => setConfirmOpen(true)}
            >
              {repointing ? <CircularProgress size={20} /> : t("ridePointer.confirmAction")}
            </Button>
          </Box>
        </Stack>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("ridePointer.confirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography sx={{ mb: 1 }}>{t("ridePointer.confirmBody")}</Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("ridePointer.confirmSourceLabel")}
                </Typography>
                <Typography color="text.primary">
                  {sourceRide
                    ? `${rideLabel(sourceRide)} (${classLabelOf(sourceRide.classId)})`
                    : "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t("ridePointer.confirmTargetLabel")}
                </Typography>
                <Typography color="text.primary">
                  {targetRide
                    ? `${rideLabel(targetRide)} (${classLabelOf(targetRide.classId)})`
                    : "-"}
                </Typography>
              </Box>
            </Stack>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t("ridePointer.confirmWarning")}
            </Alert>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          {/* #837 — explicit text.primary: the brand theme's default Button
              text color reads too low-contrast against the dark-mode dialog
              background (a repeat of the known light/dark contrast gotcha —
              always force text.primary rather than trust the default). */}
          <Button sx={{ color: "text.primary" }} onClick={() => setConfirmOpen(false)}>
            {t("ridePointer.cancel")}
          </Button>
          <Button variant="contained" onClick={handleConfirm} disabled={repointing}>
            {repointing ? <CircularProgress size={20} /> : t("ridePointer.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
